import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Phone, MapPin } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "March 17, 2026";
  
  return (
    <div data-testid="privacy-policy-page" className="bg-gray-50 min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#0A0A0A] to-gray-900 text-white py-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#D92323]/20 text-[#D92323] px-4 py-2 rounded-full mb-6">
              <Shield size={18} />
              <span className="font-semibold text-sm">Legal Document</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-400">Last Updated: {lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
            
            <div className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-600 mb-6">
                Welcome to A Digital ("we," "our," or "us"), operated by Assistive Netspeed Technologies Private Limited. 
                We are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
                use our IPTV services, website (www.adigital.tv), and ANT TV application.
              </p>
              <p className="text-gray-600 mb-6">
                By accessing or using our services, you agree to this Privacy Policy. If you do not agree with the 
                terms of this policy, please do not access our services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Personal Information</h3>
              <p className="text-gray-600 mb-4">We may collect the following personal information:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li>Name and contact information (email address, phone number, address)</li>
                <li>Account credentials (username, password)</li>
                <li>Billing and payment information</li>
                <li>Device information (device type, operating system, unique device identifiers)</li>
                <li>Location information (city, state, PIN code)</li>
                <li>Service usage data and viewing preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Automatically Collected Information</h3>
              <p className="text-gray-600 mb-4">When you use our services, we automatically collect:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li>IP address and browser type</li>
                <li>Device identifiers and hardware information</li>
                <li>Usage statistics and viewing history</li>
                <li>App performance data and error logs</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">3. How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">We use the collected information for the following purposes:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li>To provide, maintain, and improve our IPTV services</li>
                <li>To process transactions and send related information</li>
                <li>To send you technical notices, updates, and support messages</li>
                <li>To respond to your comments, questions, and customer service requests</li>
                <li>To communicate with you about products, services, and promotional offers</li>
                <li>To monitor and analyze usage patterns and trends</li>
                <li>To detect, prevent, and address technical issues and security threats</li>
                <li>To comply with legal obligations and enforce our terms of service</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-600 mb-4">We may share your information in the following circumstances:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li><strong>Service Providers:</strong> With third-party vendors who assist in providing our services (payment processors, cloud hosting, analytics)</li>
                <li><strong>Business Partners:</strong> With broadcasters and content providers as required for service delivery</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
                <li><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of company assets</li>
              </ul>
              <p className="text-gray-600 mb-6">
                We do not sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">5. Data Security</h2>
              <p className="text-gray-600 mb-6">
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and employee training</li>
                <li>Incident response procedures</li>
              </ul>
              <p className="text-gray-600 mb-6">
                However, no method of transmission over the Internet or electronic storage is 100% secure. 
                While we strive to protect your information, we cannot guarantee absolute security.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">6. Data Retention</h2>
              <p className="text-gray-600 mb-6">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in 
                this Privacy Policy, unless a longer retention period is required or permitted by law. When your 
                information is no longer needed, we will securely delete or anonymize it.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">7. Your Rights and Choices</h2>
              <p className="text-gray-600 mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Opt-out:</strong> Opt out of marketing communications</li>
                <li><strong>Data Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where applicable</li>
              </ul>
              <p className="text-gray-600 mb-6">
                To exercise these rights, please contact us using the information provided below.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">8. Cookies and Tracking Technologies</h2>
              <p className="text-gray-600 mb-6">
                We use cookies and similar tracking technologies to collect and store information about your 
                preferences and usage patterns. You can control cookies through your browser settings. However, 
                disabling cookies may affect the functionality of our services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">9. Third-Party Links and Services</h2>
              <p className="text-gray-600 mb-6">
                Our services may contain links to third-party websites or services. We are not responsible for 
                the privacy practices of these third parties. We encourage you to review their privacy policies 
                before providing any personal information.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">10. Children's Privacy</h2>
              <p className="text-gray-600 mb-6">
                Our services are not directed to children under 13 years of age. We do not knowingly collect 
                personal information from children under 13. If we become aware that we have collected personal 
                information from a child under 13, we will take steps to delete such information promptly.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">11. International Data Transfers</h2>
              <p className="text-gray-600 mb-6">
                Your information may be transferred to and processed in countries other than your country of 
                residence. These countries may have different data protection laws. By using our services, you 
                consent to the transfer of your information to these countries.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-600 mb-6">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued 
                use of our services after such changes constitutes acceptance of the updated policy.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">13. Contact Us</h2>
              <p className="text-gray-600 mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                please contact us:
              </p>
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-gray-900 mb-4">Assistive Netspeed Technologies Private Limited</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-600">
                    <Mail size={18} className="text-[#D92323]" />
                    <span>privacy@adigital.tv</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <Mail size={18} className="text-[#D92323]" />
                    <span>info@adigital.tv</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <Phone size={18} className="text-[#D92323]" />
                    <span>+91 9019206699</span>
                  </div>
                  <div className="flex items-start gap-3 text-gray-600">
                    <MapPin size={18} className="text-[#D92323] mt-1" />
                    <span>Bangalore, Karnataka, India</span>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">14. Grievance Officer</h2>
              <p className="text-gray-600 mb-6">
                In accordance with the Information Technology Act, 2000 and rules made thereunder, the Grievance 
                Officer for the purpose of this Privacy Policy shall be:
              </p>
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <p className="text-gray-600">
                  <strong>Email:</strong> grievance@adigital.tv<br />
                  <strong>Response Time:</strong> Within 30 days of receiving the complaint
                </p>
              </div>

            </div>

            {/* Footer Links */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/" className="text-[#D92323] hover:underline font-medium">Home</Link>
                <span className="text-gray-300">|</span>
                <Link to="/terms" className="text-[#D92323] hover:underline font-medium">Terms of Service</Link>
                <span className="text-gray-300">|</span>
                <Link to="/contact" className="text-[#D92323] hover:underline font-medium">Contact Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
